import { useState, useRef, useEffect } from 'react'
import { getActiveMeeting } from '../services/api'
import OsteoLogo from '../components/Logo'

const CASE_ID_REGEX = /^[A-Z0-9][A-Z0-9_-]{2,49}$/

export default function JoinMeetingPage() {
  const [caseId,  setCaseId]  = useState('')
  const [pin,     setPin]     = useState('')
  const [error,   setError]   = useState(null)
  const [loading, setLoading] = useState(false)
  const [view,    setView]    = useState('form') // 'form' | 'notfound' | 'found'
  const [meeting, setMeeting] = useState(null)
  const caseRef = useRef(null)
  const pinRef  = useRef(null)

  function handleCaseChange(e) {
    setCaseId(e.target.value.toUpperCase().replace(/\s/g, ''))
    setError(null)
  }

  function handleCaseKeyDown(e) {
    if (caseId === '' && !e.ctrlKey && !e.metaKey && e.key !== 'Backspace' && !e.key.startsWith('Arrow')) {
      const char = e.key.toUpperCase()
      if (/^[A-Z0-9]$/.test(char) && char !== 'O') {
        e.preventDefault()
        setCaseId('O3D-' + char)
      }
    }
  }

  function handlePinChange(e) {
    const v = e.target.value.replace(/\D/g, '').slice(0, 6)
    setPin(v)
    setError(null)
  }

  async function lookup() {
    setError(null)

    const trimmedId = caseId.trim()
    if (!trimmedId) {
      setError('Please enter your Case ID.')
      caseRef.current?.focus()
      return
    }
    if (!CASE_ID_REGEX.test(trimmedId)) {
      setError('Invalid Case ID format. It looks like O3D-K7QM2P9X.')
      caseRef.current?.focus()
      return
    }
    if (!/^\d{6}$/.test(pin)) {
      setError('Enter the 6-digit PIN your coordinator shared with you.')
      pinRef.current?.focus()
      return
    }

    setLoading(true)
    try {
      const { data } = await getActiveMeeting(trimmedId, pin)
      setMeeting(data.meeting || null)
      setView(data.meeting ? 'found' : 'notfound')
    } catch (err) {
      const msg = err.response?.data?.error || 'Unable to find your meeting. Please try again.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  function handleSubmit(e) {
    e.preventDefault()
    lookup()
  }

  // Server is the source of truth for canJoin — re-poll while waiting for the window to open.
  useEffect(() => {
    if (view !== 'found' || meeting?.canJoin) return
    const id = setInterval(lookup, 30000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, meeting?.canJoin])

  function reset() {
    setMeeting(null)
    setView('form')
    setError(null)
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="card max-w-md w-full">
        <div className="text-center mb-8">
          <a
            href="https://www.osteo3d.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-brand-500 transition-colors mb-4"
          >
            ← Back to osteo3d.com
          </a>
          <div className="flex justify-center mb-5">
            <a href="https://www.osteo3d.com" target="_blank" rel="noopener noreferrer">
              <OsteoLogo size="lg" />
            </a>
          </div>
          <h2 className="text-lg font-semibold text-gray-700">Join Planning Meeting</h2>
          <p className="text-gray-400 text-sm mt-0.5">Enter your Case ID and PIN to find your meeting</p>
        </div>

        {view === 'form' && (
          <form onSubmit={handleSubmit} noValidate>
            <label htmlFor="caseId" className="block text-sm font-medium text-gray-700 mb-1.5">Case ID</label>
            <input
              ref={caseRef}
              id="caseId"
              type="text"
              value={caseId}
              onChange={handleCaseChange}
              onKeyDown={handleCaseKeyDown}
              placeholder="O3D-K7QM2P9X"
              maxLength={50}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              disabled={loading}
              className="input font-mono tracking-widest text-center text-lg"
            />

            <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-1.5 mt-4">6-digit PIN</label>
            <input
              ref={pinRef}
              id="pin"
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              value={pin}
              onChange={handlePinChange}
              placeholder="123456"
              maxLength={6}
              autoComplete="off"
              disabled={loading}
              className="input font-mono tracking-[0.5em] text-center text-lg"
            />

            {error && (
              <p className="mt-3 text-sm text-red-600 flex items-start gap-1.5">
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </p>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full mt-4">
              {loading ? <span className="flex items-center gap-2"><Spinner /> Looking up…</span> : 'Find My Meeting'}
            </button>
          </form>
        )}

        {view === 'notfound' && (
          <div className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-700 mb-2">No upcoming meeting</h3>
            <p className="text-sm text-gray-500 mb-5">
              No planning meeting is scheduled for this case. Please contact us at{' '}
              <a href="mailto:contactus@osteo3d.com" className="text-brand-500 hover:underline">contactus@osteo3d.com</a>.
            </p>
            <button onClick={reset} className="btn-secondary w-full">Try another Case ID</button>
          </div>
        )}

        {view === 'found' && meeting && <MeetingCard meeting={meeting} onReset={reset} />}

        <p className="mt-5 text-center text-xs text-gray-400">
          Your Case ID and PIN were provided by your Osteo3d coordinator.
          <br />
          Contact them if you don&apos;t have them.
        </p>
      </div>

      <a href="/admin/login" className="mt-8 text-xs text-gray-300 hover:text-gray-400 transition-colors">
        Admin portal
      </a>
    </div>
  )
}

function MeetingCard({ meeting, onReset }) {
  const scheduled = new Date(meeting.scheduledAt)
  const formatted = scheduled.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })

  function joinNow() {
    if (meeting.meetUrl) {
      window.open(meeting.meetUrl, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <>
      <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 mb-4">
        <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Scheduled for</div>
        <div className="text-base font-semibold text-gray-700">{formatted}</div>
        <div className="text-sm text-gray-500 mt-0.5">{meeting.durationMin} minutes</div>
      </div>

      {meeting.canJoin ? (
        <>
          <button onClick={joinNow} className="btn-primary w-full bg-green-600 hover:bg-green-700 focus:ring-green-500">
            Join Google Meet Now
          </button>
          <p className="text-xs text-gray-500 mt-3 text-center">
            Don&apos;t have a Google account? You can still join — just enter your name when prompted.
            The host will admit you.
          </p>
        </>
      ) : (
        <>
          <button disabled className="btn-primary w-full opacity-50 cursor-not-allowed">
            Join opens 15 minutes before the meeting
          </button>
          <p className="text-xs text-gray-500 mt-3 text-center">
            <CountdownText to={new Date(meeting.joinWindowStart)} />
          </p>
        </>
      )}

      <button onClick={onReset} className="mt-4 text-xs text-gray-400 hover:text-gray-600 w-full text-center">
        ← Look up a different case
      </button>
    </>
  )
}

function CountdownText({ to }) {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000)
    return () => clearInterval(id)
  }, [])

  const diffMs = to - now
  if (diffMs <= 0) return <span>Join opens momentarily…</span>

  const totalMin = Math.ceil(diffMs / 60000)
  if (totalMin < 60) return <span>Join opens in {totalMin} minute{totalMin === 1 ? '' : 's'}</span>

  const hours = Math.floor(totalMin / 60)
  if (hours < 24) return <span>Join opens in about {hours} hour{hours === 1 ? '' : 's'}</span>

  const days = Math.floor(hours / 24)
  return <span>Join opens in about {days} day{days === 1 ? '' : 's'}</span>
}

function Spinner() {
  return (
    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  )
}
